/**
 * Sticky add to cart bar for the product page.
 *
 * Slides up once the real add to cart button has scrolled off the top, and hides again while the
 * footer is on screen so it never covers it. Its button does not add anything: it scrolls back up to
 * the buy box (colour, sizes, quantity) so the buyer chooses there and adds from the real button.
 *
 * Vanilla: no imports, no framework, no theme runtime.
 */
if (!customElements.get('sustena-pdp-sticky')) {
  class SustenaPdpSticky extends HTMLElement {
    #mobile = window.matchMedia('(max-width: 749px)');
    /** @type {IntersectionObserver | null} */
    #buttonObserver = null;
    /** @type {IntersectionObserver | null} */
    #blockerObserver = null;
    #pastButton = false;
    /** @type {Set<Element>} */
    #blockersInView = new Set();

    connectedCallback() {
      // `position: fixed` only sticks to the viewport when no ancestor has a transform or a filter,
      // and the product layout is free to grow one. Moving the bar to the body removes the question.
      if (this.parentElement !== document.body) document.body.appendChild(this);

      this.#mobile.addEventListener('change', this.#update);
      document.addEventListener('shopify:section:load', this.#refresh);
      document.addEventListener('shopify:section:unload', this.#refresh);
      this.addEventListener('click', this.#handleClick);
      this.#observe();
    }

    disconnectedCallback() {
      this.#mobile.removeEventListener('change', this.#update);
      document.removeEventListener('shopify:section:load', this.#refresh);
      document.removeEventListener('shopify:section:unload', this.#refresh);
      this.#buttonObserver?.disconnect();
      this.#blockerObserver?.disconnect();
    }

    #refresh = () => {
      requestAnimationFrame(() => this.#observe());
    };

    /** @returns {HTMLButtonElement | null} */
    get #addToCartButton() {
      return document.querySelector('product-form-component button[name="add"]');
    }

    #observe() {
      this.#buttonObserver?.disconnect();
      this.#blockerObserver?.disconnect();
      this.#blockersInView.clear();

      const target = this.#addToCartButton;

      // The root is stretched far below the viewport, so the button counts as intersecting the whole
      // time it is at or below the top edge and stops the moment it goes above it. Watching plain
      // intersection instead would miss a long jump — anchor links, restored scroll position — where
      // the button is off screen before and after, so no change is reported and nothing fires.
      this.#buttonObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          if (!entry) return;
          this.#pastButton = !entry.isIntersecting;
          this.#update();
        },
        { rootMargin: '0px 0px 100000px 0px' }
      );

      this.#blockerObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#blockersInView.add(entry.target);
          } else {
            this.#blockersInView.delete(entry.target);
          }
        }
        this.#update();
      });

      if (target) this.#buttonObserver.observe(target);
      for (const blocker of document.querySelectorAll('body footer')) this.#blockerObserver.observe(blocker);
      this.#update();
    }

    #update = () => {
      const visible = this.#mobile.matches && this.#pastButton && this.#blockersInView.size === 0;
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
    };

    /** @param {Event} event */
    #handleClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-sustena-sticky-add]')) return;

      event.preventDefault();

      const destination =
        document.querySelector('sustena-buy-box .sustena-bb__colors') ||
        document.querySelector('sustena-buy-box') ||
        this.#addToCartButton;
      if (!destination) return;

      // The header comes back while scrolling up, so leave room for it above the target.
      const header = document.querySelector('#header-component');
      const offset = (header ? header.getBoundingClientRect().height : 0) + 16;
      const top = destination.getBoundingClientRect().top + window.scrollY - offset;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
    };
  }

  customElements.define('sustena-pdp-sticky', SustenaPdpSticky);
}
